import React from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, Settings as SettingsIcon, BarChart3, ChevronRight } from "lucide-react";
import { openSettings } from "@/lib/cookieConsent";
import useSEO from "@/lib/seo";

const SKYLINE_URL = "/assets/skyline-baustelle.png";

function Category({ icon, title, badge, children }) {
  return (
    <div className="border border-brand-line bg-white p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[hsl(var(--brand-soft))] flex items-center justify-center text-[hsl(var(--brand-green-strong))]">
            {icon}
          </div>
          <h3 className="font-semibold text-xl">{title}</h3>
        </div>
        {badge && <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mt-2">{badge}</span>}
      </div>
      <div className="text-sm text-neutral-700 leading-relaxed space-y-3 pl-[3.25rem]">
        {children}
      </div>
    </div>
  );
}

export default function Cookies() {
  useSEO({
    title: "Cookie-Richtlinie",
    description: "Transparente Informationen darüber, welche Cookies wir verwenden — Notwendig, Funktional und Analyse. Einstellungen jederzeit änderbar.",
    path: "/cookies",
  });
  return (
    <div data-testid="cookies-page">
      {/* Hero with Skyline */}
      <section className="relative bg-[hsl(var(--brand-soft))] overflow-hidden">
        <div className="container-narrow pt-16 lg:pt-24 pb-32 lg:pb-44 relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-5">
            <Cookie className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))]">Cookie-Richtlinie</span>
          </div>
          <h1 className="font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
            Transparenz über Cookies auf dieser Webseite
          </h1>
          <p className="text-lg text-neutral-700 leading-relaxed max-w-2xl">
            Wir möchten, dass Sie genau wissen, welche Cookies wir verwenden und wofür. Auf dieser Seite erklären wir verständlich, welche Daten erhoben werden und welche Auswahlmöglichkeiten Sie haben.
          </p>
          <div className="mt-8">
            <button
              onClick={openSettings}
              data-testid="cookies-open-settings"
              className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium"
            >
              Cookie-Einstellungen öffnen <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Decorative skyline at bottom */}
        <div className="absolute inset-x-0 bottom-0 pointer-events-none select-none">
          <img
            src={SKYLINE_URL}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="w-full h-auto block opacity-90"
          />
        </div>
      </section>

      {/* Categories */}
      <section className="container-narrow py-16 lg:py-20 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Cookie-Kategorien</p>
        <h2 className="font-semibold text-2xl sm:text-3xl mb-2">Diese Kategorien gibt es</h2>
        <p className="text-sm text-neutral-600 mb-10 max-w-2xl">
          Bei Ihrem ersten Besuch entscheiden Sie, welche Kategorien Sie zulassen möchten. Sie können diese Auswahl jederzeit über den Button „Cookie-Einstellungen" unten im Seitenfuß ändern.
        </p>

        <div className="space-y-5">
          <Category icon={<Shield className="h-5 w-5" />} title="Notwendige Cookies" badge="Immer aktiv">
            <p>
              Diese Cookies sind technisch erforderlich, damit die Webseite funktioniert. Dazu gehören etwa Ihre Cookie-Auswahl, sitzungsbezogene Daten und Sicherheits-Token für den Admin-Bereich.
            </p>
            <p className="text-xs text-neutral-500">
              <strong>Speichermedium:</strong> Lokaler Browser-Speicher (localStorage)<br />
              <strong>Speicherdauer:</strong> bis zum manuellen Löschen oder Ablauf
            </p>
          </Category>

          <Category icon={<SettingsIcon className="h-5 w-5" />} title="Funktionale Cookies" badge="Optional">
            <p>
              Funktionale Cookies ermöglichen erweiterte Inhalte. Auf dieser Webseite betrifft das insbesondere die Karten-Darstellung auf der Seite <Link to="/liveverkehr" className="text-[hsl(var(--brand-green-strong))] underline">LIVE-Verkehr</Link>: Beim Aufruf werden Karten-Kacheln von OpenStreetMap nachgeladen und externe Routing-Daten (z. B. TomTom-Verkehrsdaten) abgerufen.
            </p>
            <p className="text-xs text-neutral-500">
              <strong>Drittanbieter:</strong> OpenStreetMap-Stiftung (Karten-Tiles), TomTom (Verkehrslage)<br />
              <strong>Wenn deaktiviert:</strong> die Karte wird nicht geladen — Sie sehen einen Platzhalter mit Hinweis.
            </p>
          </Category>

          <Category icon={<BarChart3 className="h-5 w-5" />} title="Analyse-Cookies" badge="Optional · derzeit nicht aktiv">
            <p>
              Anonymisierte Statistiken helfen uns zu verstehen, welche Inhalte besonders oft gelesen werden. So können wir die Webseite zielgerichtet weiterentwickeln.
            </p>
            <p>
              Aktuell setzen wir <strong>keine</strong> Analyse-Tools wie Google Analytics oder Matomo ein. Sollten wir das in Zukunft tun, werden Statistiken ausschließlich anonymisiert erhoben und nur mit Ihrer ausdrücklichen Zustimmung aktiviert.
            </p>
          </Category>
        </div>

        {/* Re-open settings */}
        <div className="mt-12 border-t border-brand-line pt-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Ihre Auswahl ändern</p>
          <h3 className="font-semibold text-xl mb-2">Cookie-Einstellungen jederzeit anpassen</h3>
          <p className="text-sm text-neutral-600 mb-5 max-w-2xl">
            Sie können Ihre Entscheidung jederzeit ändern. Klicken Sie dazu auf den folgenden Button — die ursprüngliche Auswahl-Maske erscheint dann erneut.
          </p>
          <button
            onClick={openSettings}
            data-testid="cookies-reopen-settings"
            className="inline-flex items-center gap-2 border border-brand-line bg-white text-neutral-800 px-5 py-3 text-sm font-medium hover:bg-neutral-50 transition-colors"
          >
            <SettingsIcon className="h-4 w-4" /> Cookie-Einstellungen öffnen
          </button>
        </div>

        {/* Rechtliches */}
        <div className="mt-12 text-sm text-neutral-600 leading-relaxed border-t border-brand-line pt-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Rechtsgrundlage</p>
          <p>
            Die Verarbeitung notwendiger Cookies erfolgt auf Grundlage unseres berechtigten Interesses gemäß Art. 6 Abs. 1 lit. f DSGVO (Bereitstellung der Webseite). Funktionale und Analyse-Cookies werden ausschließlich auf Grundlage Ihrer Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TTDSG verarbeitet. Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.
          </p>
          <p className="mt-3">
            Weitere Informationen zur Datenverarbeitung finden Sie in unseren{" "}
            <Link to="/datenschutz" className="text-[hsl(var(--brand-green-strong))] underline">Datenschutzhinweisen</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
